import { useQuery } from '@tanstack/react-query';
import { hasRole, useSession } from '@/shared/auth/SessionProvider';
import { useActiveCountry } from '@/shared/countries/ActiveCountryProvider';
import {
  listPendingCambiosHorno,
  listPendingDescuentos,
  listPendingEliminaciones,
  listPendingFacturacionPrevia,
  listPendingFacturaPorcentaje,
} from './api';
import { listEcheqs } from '@/shared/api/controllers/echeqs';

/**
 * Total count of pending approval requests across every queue, used to
 * drive the "Aprobaciones" sidebar badge. Must list ALL types the page
 * renders — `cambio-horno` was missing since it shipped, so those requests
 * never bumped the badge (fixed together with `facturacion-previa`).
 *
 * Shares React Query cache with AprobacionesPage: the keys
 * `['aprobaciones', countryCode, 'eliminaciones']` etc. match exactly,
 * so opening the page reads from the same cache instead of triggering a
 * second fetch. Any mutation inside the module already calls
 * `invalidateQueries({ queryKey: ['aprobaciones'] })`, which refreshes
 * this badge automatically.
 *
 * Country in the queryKey is defense-in-depth on top of
 * `queryClient.clear()` at country-switch time — without it, the badge
 * would briefly serve stale data from the previous country between the
 * cache clear and the next refetch (2026-05-11: surfaced as "badge says
 * 1 but MX page is empty" because an AR descuento was still cached).
 *
 * Only admin / administración roles fetch; vendedores and others get
 * `count = 0` without hitting the network.
 */
export function usePendingAprobacionesCount(): number {
  const { profile } = useSession();
  const { config: activeCountry } = useActiveCountry();
  const countryCode = activeCountry.code;
  const canSee = hasRole(profile, ['admin', 'administracion']);

  const eliminacionesQ = useQuery({
    queryKey: ['aprobaciones', countryCode, 'eliminaciones'],
    queryFn: listPendingEliminaciones,
    enabled: canSee,
    staleTime: 30_000,
  });
  const descuentosQ = useQuery({
    queryKey: ['aprobaciones', countryCode, 'descuentos'],
    queryFn: listPendingDescuentos,
    enabled: canSee,
    staleTime: 30_000,
  });
  const facturaPctQ = useQuery({
    queryKey: ['aprobaciones', countryCode, 'factura-pct'],
    queryFn: listPendingFacturaPorcentaje,
    enabled: canSee,
    staleTime: 30_000,
  });
  const cambioHornoQ = useQuery({
    queryKey: ['aprobaciones', countryCode, 'cambio-horno'],
    queryFn: listPendingCambiosHorno,
    enabled: canSee,
    staleTime: 30_000,
  });
  // AR-only, pero la query corre igual: el backend devuelve [] fuera de AR
  // y así el key queda cacheado igual que el resto.
  const facturacionPreviaQ = useQuery({
    queryKey: ['aprobaciones', countryCode, 'facturacion-previa'],
    queryFn: listPendingFacturacionPrevia,
    enabled: canSee,
    staleTime: 30_000,
  });

  const echeqsQ = useQuery({
    queryKey: ['aprobaciones', countryCode, 'echeqs'],
    queryFn: () => listEcheqs({ estado: ['Pendiente Autorizacion'] }),
    enabled: canSee,
    staleTime: 30_000,
  });

  return (
    (echeqsQ.data?.length ?? 0) +
    (eliminacionesQ.data?.length ?? 0) +
    (descuentosQ.data?.length ?? 0) +
    (facturaPctQ.data?.length ?? 0) +
    (cambioHornoQ.data?.length ?? 0) +
    (facturacionPreviaQ.data?.length ?? 0)
  );
}
